import type { ReactElement } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { InfoLayout } from "@/components/laneguide/InfoLayout";
import { ChevronDown } from "lucide-react";

export const Route = createFileRoute("/ordlista")({
  head: () => ({ meta: [{ title: "Ordlista – Låne- och finansbegrepp | Svensk Låneguide" }, { name: "description", content: "Komplett ordlista över de vanligaste låne- och finansbegreppen." }] }),
  component: Ordlista,
});

type Entry = { term: string; body: ReactElement };

const ENTRIES: Entry[] = [
  { term: "Amortering", body: <p>Den del av <Link to="/privatlan">privatlånet</Link> du betalar tillbaka varje månad – utöver räntan.</p> },
  { term: "Annuitetslån", body: <p>Ett lån där du betalar lika mycket varje månad hela löptiden. Räntedelen minskar över tid medan amorteringen ökar. Vanligast bland <Link to="/privatlan">privatlån</Link>.</p> },
  { term: "Betalningsanmärkning", body: <p>En notering hos kreditupplysningsföretag om att en skuld inte betalats. Påverkar möjligheten att få lån och hyra bostad. Se <Link to="/lan-utan-uc">lån utan UC</Link>.</p> },
  { term: "BankID", body: <p>Svensk e-legitimation som används för att signera <Link to="/snabblan">snabblån</Link> och andra digitala avtal.</p> },
  { term: "Direktutbetalning", body: <p>Utbetalning inom minuter efter godkänd ansökan – kräver oftast samma bank som långivaren. Läs mer om <Link to="/snabblan">snabblån med direktutbetalning</Link>.</p> },
  { term: "Dröjsmålsränta", body: <p>Extra ränta som tas ut när en betalning är försenad. Regleras av räntelagen.</p> },
  { term: "Effektiv ränta", body: <p>Total årskostnad för lånet inklusive alla avgifter (uppläggning, avisering). Använd alltid effektiv ränta för att jämföra lån rättvist.</p> },
  { term: "Kontantinsats", body: <p>Den del av köpet du betalar själv, t.ex. vid <Link to="/billan">billån</Link>. Större kontantinsats ger lägre ränta.</p> },
  { term: "Kreditprövning", body: <p>Lagstadgad kontroll av inkomst och skulder innan lån beviljas.</p> },
  { term: "Kreditupplysning", body: <p>Rapport från kreditbyrå (t.ex. UC, Creditsafe) om din ekonomiska situation.</p> },
  { term: "Löptid", body: <p>Hur länge lånet ska betalas tillbaka. Längre löptid ger lägre månadskostnad men högre totalkostnad.</p> },
  { term: "Nominell ränta", body: <p>Basräntan på lånet, utan avgifter. Använd inte denna för jämförelse – använd <b>effektiv ränta</b>.</p> },
  { term: "Rak amortering", body: <p>Amortering med samma belopp varje månad. Månadskostnaden minskar över tid.</p> },
  { term: "Restskuld", body: <p>Skuld som kvarstår efter att du sålt t.ex. bilen – om bilens värde understiger lånets storlek.</p> },
  { term: "Samlingslån", body: <p>Ett lån som löser flera mindre krediter. Läs vår <Link to="/samla-lan">guide till samlingslån</Link>.</p> },
  { term: "Skuldkvot", body: <p>Dina totala skulder i förhållande till din årsinkomst.</p> },
  { term: "SMS-lån", body: <p>Mindre snabblån som ansöks via mobilen. Se <Link to="/smslan">våra jämförda SMS-lån</Link>.</p> },
  { term: "UC", body: <p>Upplysningscentralen – Sveriges största kreditupplysningsbolag. Vill du inte påverka din UC-poäng, se <Link to="/lan-utan-uc">lån utan UC</Link>.</p> },
  { term: "Uppläggningsavgift", body: <p>Engångsavgift vid tecknande av lånet, får max vara 1 000 kr enligt konsumentkreditlagen.</p> },
  { term: "Ångerrätt", body: <p>Enligt lag har du 14 dagars ångerrätt på konsumentkrediter. Långivaren måste informeras skriftligen.</p> },
];

function Ordlista() {
  const [open, setOpen] = useState<string | null>(null);
  return (
    <InfoLayout title="Ordlista – låne- och finansbegrepp">
      <p>Klicka på ett ord för att läsa en kort förklaring med länkar till fördjupande sidor.</p>
      <div className="mt-6 space-y-2">
        {ENTRIES.map((e) => {
          const isOpen = open === e.term;
          return (
            <div key={e.term} className="rounded-md border border-border bg-card">
              <button onClick={() => setOpen(isOpen ? null : e.term)} className="flex w-full items-center justify-between px-4 py-3 text-left">
                <span className="font-display font-bold text-foreground">{e.term}</span>
                <ChevronDown className={`h-4 w-4 text-primary transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>
              {isOpen && <div className="border-t border-border px-4 py-3 text-sm text-foreground/80 [&_a]:text-primary [&_a]:underline">{e.body}</div>}
            </div>
          );
        })}
      </div>
    </InfoLayout>
  );
}
